import {
  colorMap,
  usageBlocks,
  masteryActivityBlocks,
  targetBlocks,
  aggregate as timeAggregate,
  sdoNs,
  products,
  productStatuses,
  status as statusConst,
  nodeStatuses,
  weeklyMinuteTargetGoals,
  landingPageProducts,
} from '@modules/constants-enumerate';
import {storeInstance} from '@aofl/store/dist/legacy';
import {numberAbbr} from './i18n-numbers';
import {dashboardAriaLabels, dashboardLabels} from './i18n-labels';
import {
  addMonths,
  addWeeks,
  startOfWeek,
  getUnixTime,
  endOfMonth,
  endOfWeek,
  addDays,
  subYears,
  endOfDay,
  format,
  startOfMonth,
  differenceInWeeks,
} from 'date-fns';
import {ApiService} from '@modules/api-service';
import {dateTimeFormatter} from '@modules/i18n-datetime';
import {unsafeHTML} from 'lit-html/directives/unsafe-html';

class DashboardUtils {
  static usageColorMapArray = [colorMap.minutes45, colorMap.minutes31, colorMap.minutes16, colorMap.minutes1];
  static activityMapArray = [colorMap.midBlue, colorMap.targetMed, colorMap.inactive];
  static mmaActivityMapArray = [colorMap.midBlue, colorMap.targetMedOrange, colorMap.inactive];
  static targetColorMapArray = [colorMap.targetHigh, colorMap.targetMed, colorMap.inactive];

  static USAGE_CHART_DATA = {
    datasets: [],
    forceUpdate: false,
    labels: ['Students', 'Students', 'Students', 'Students'],
  };

  static getBarChartOptions(chartType) {
    return {
      responsive: true,
      aspectRatio: 5,
      maintainAspectRatio: true,
      layout: {
        padding: {
          top: 15
        }
      },
      scales: {
        xAxis: {
          grid: {
            drawBorder: true,
            borderColor: '#979797',
            display: false,
          },
          ticks: {
            color: '#000',
            font: {
              family: 'aofl-sans',
              size: 14,
            },
          }
        },
        yAxis: {
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            display: false,
          }
        },
      },
      datasets: {
        bar: {
          barPercentage: .75
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: false
        },
      },
      animation: {
        duration: 1,
        onComplete: function(e) {
          const chartInstance = e.chart;
          const ctx = chartInstance.ctx;

          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';

          chartInstance.data.datasets.forEach(function(dataset, i) {
            const meta = chartInstance.getDatasetMeta(i);
            meta.data.forEach(function(bar, index) {
              const data = dataset.data[index];
              if (data) {
                const chartLabelAppend = chartType === 'time' ? 'm' : '';
                ctx.fillText(data + chartLabelAppend, bar.x, bar.y);
              }
            });
          });
        }
      },
    };
  }

  static getPercentageLabels(args) {
    const {data} = args;
    if (!data) {
      return {};
    }

    const total = Object.values(data).reduce((a, b) => a + b, 0);
    const percentageMap = {};
    const percentageLabelMap = {};
    let totalPercent = 0;
    let maxPercentage = 0;
    let maxPercentageKey = '';
    for (const i in data) {
      if (!data.hasOwnProperty(i)) continue;
      const val = data[i];
      let percent = 0;
      let percentLabel = 0;
      if (val && total) {
        percent = Math.round(val / total * 100);
        percentLabel = String(percent);
        // We don't want to show zero for percentages that are less
        // than 1%
        if (percent === 0) {
          percent = 1;
          percentLabel = 0.5;
        }
      }
      totalPercent += percent;
      percentageLabelMap[i] = percentLabel;
      percentageMap[i] = percent;
      if (percent > maxPercentage) {
        maxPercentageKey = i;
        maxPercentage = percent;
      }
    }
    // if the rounding does not result in 100%, recalculate and (+/-) to the
    // item with the most
    if (totalPercent !== 100 && totalPercent > 0) {
      percentageLabelMap[maxPercentageKey] = String(percentageMap[maxPercentageKey] + 100 - totalPercent);
    }
    return percentageLabelMap;
  }

  static getRangeLegend(ranges) {
    const legend = [];
    const rangeKeys = Object.keys(ranges);

    let i = 0;
    for (const [key, sec] of Object.entries(ranges)) {
      let label = '';
      const colorKey = 'range' + key.charAt(0).toUpperCase() + key.slice(1);
      if (typeof rangeKeys[i+1] === 'undefined') {
        label =  '≥' + Math.floor(sec / 60).toString();
      } else {
        label = Math.floor(sec / 60)
          .toString() + '-';
        label += Math.floor(((ranges[rangeKeys[i + 1]]) / 60) - 1)
          .toString();
      }
      if (label.charAt(0) === '0') {
        label = '>' + label;
      }
      // Add the legend
      legend[i] = {
        color: colorMap[colorKey],
        label: dashboardLabels.rangeLabel(label),
      };
      i++;
    }
    return legend;
  }

  static getRoundedMinutes(secondsToAverage) {
    return secondsToAverage < 60 ? Math.ceil(secondsToAverage / 60) : Math.floor(secondsToAverage / 60);
  }

  static getBorderWidths(comparate, doughnutValues) {
    const borderWidths = [];
    doughnutValues.map((value) => {
      borderWidths.push((value === comparate) ? 8 : 0);
    });
    return borderWidths;
  }

  static getBorderAligns(comparate, doughnutValues) {
    const borderAligns = [];
    doughnutValues.map((value) => {
      borderAligns.push((value === comparate) ? 'center' : 'inner');
    });
    return borderAligns;
  }

  static getMasteryActivityLegend(_args) {
    const {data, labels, filter, language} = _args;
    const average = Object.values(data.statuses).reduce((a, b) => a + b);
    let extraLabel = '';
    if (data?.removedInWeek) {
      extraLabel = '**';
    } else if (data?.has_removed_students || data?.has_unlicensed_students) {
      extraLabel = '*';
    }
    numberAbbr.lang = language;
    const abbreviatedAverage = numberAbbr.abbr(average);
    abbreviatedAverage.postfix += extraLabel;
    const keys = [
      {
        value: data.statuses.in_progress,
        label: labels.in_progress,
        description: dashboardLabels.PROGRESSING,
        filterVal: masteryActivityBlocks.IN_PROGRESS,
        active: (filter === masteryActivityBlocks.IN_PROGRESS),
        ariaLabel: dashboardAriaLabels.progressing(labels.in_progress),
      },
      {
        value: data.statuses.struggling,
        label: labels.struggling,
        description: dashboardLabels.STRUGGLING,
        filterVal: masteryActivityBlocks.STRUGGLING,
        active: (filter === masteryActivityBlocks.STRUGGLING),
        ariaLabel: dashboardAriaLabels.struggling(labels.struggling),
      },
      {
        value: data.statuses.inactive,
        label: labels.inactive,
        description: dashboardLabels.INACTIVE,
        filterVal: masteryActivityBlocks.INACTIVE,
        active: (filter === masteryActivityBlocks.INACTIVE),
        ariaLabel: dashboardAriaLabels.inactive(labels.inactive),
      },
    ];


    return {
      average: abbreviatedAverage,
      label: dashboardLabels.TOTAL_STUDENTS,
      keys,
    };
  }

  static getUsageLegend(args) {
    let {keys, filter, averageSeconds, language, footerNotices, removedInWeek} = args;
    numberAbbr.lang = language;

    const average = DashboardUtils.getRoundedMinutes(averageSeconds);
    const abbreviation = numberAbbr.abbr(average);
    if (typeof removedInWeek !== 'undefined' && removedInWeek) {
      abbreviation.postfix += '**';
    } else if (footerNotices?.usage?.has('*')) {
      abbreviation.postfix += '*';
    }
    return {
      average: abbreviation,
      label: dashboardLabels.ACTIVITY_TIME_PER_STUDENT,
      keys: keys.map((key) => {
        return {
          value: key.value || 0,
          label: ((key.percentage > 0 && key.percentage < 1) || (key.value > 0 && key.percentage === 0)) ? '<1' : key.percentage || 0,
          description: key.description,
          filterVal: key.filterVal,
          active: filter === key.filterVal,
          ariaLabel: key.ariaLabel,
        };
      })
    };
  }

  static getStats(
    statsToRetrieve,
    product,
    searchParams,
    timeParams = {start: null, end: null}
  ) {
    const state = storeInstance.getState();
    const selectedGroup = state[sdoNs.DASHBOARD].$activeGroupId;
    if (statsToRetrieve === 'usage') {
      return ApiService.statsGetUsage(
        parseInt(selectedGroup, 10),
        product,
        searchParams,
        timeParams.start,
        timeParams.end,
      );
    }
    return ApiService.statsGetActivity(
      parseInt(selectedGroup, 10),
      product,
      searchParams,
      timeParams.start,
      timeParams.end
    );
  }

  static getChartUsageData(args) {
    const {usage, filter} = args;
    const donutValues = args?.donutValues ||
      [usageBlocks.MINUTES_45, usageBlocks.MINUTES_31, usageBlocks.MINUTES_16, usageBlocks.MINUTES_1];
    const data = (usage && usage.length) ?
      usage :
      [0, 0, 0, 0];

    return Object.assign({}, DashboardUtils.USAGE_CHART_DATA, {
      datasets: [
        {
          data,
          backgroundColor: DashboardUtils.usageColorMapArray,
          hoverBackgroundColor: DashboardUtils.usageColorMapArray,
          borderColor: DashboardUtils.usageColorMapArray,
          borderWidth: DashboardUtils.getBorderWidths(filter, donutValues),
          borderAlign: DashboardUtils.getBorderAligns(filter, donutValues),
          filters: donutValues,
          ariaLabel: args.ariaLabel,
          squareAriaLabels: args.squareAriaLabels
        }
      ],
      ariaLabels: args.ariaLabels,
    });
  }

  static convertDateToUTC(date) {
    return date.setTime(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  }

  static timestampToDate(timestamp) {
    return new Date(timestamp * 1000);
  }

  static convertUTCTimestampToDate(timestamp) {
    const date = this.timestampToDate(timestamp);
    const newTimestamp = date.setTime(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
    return new Date(newTimestamp);
  }

  static convertYYYYMMDDToFormat(YYYYMMDD, newFormat) {
    const newDate = new Date(YYYYMMDD.slice(0, 4), YYYYMMDD.slice(4, 6) - 1, YYYYMMDD.slice(6-8));
    return format(newDate, newFormat);
  }

  static convertToChartLabelFormat(labelDate, monthLabelSize = 'short') {
    const labelStartDate = new Date(labelDate.slice(0, 4), labelDate.slice(4, 6) - 1, labelDate.slice(6-8));
    const labelEndDate = endOfWeek(labelStartDate);
    const opts = {month: monthLabelSize, day: 'numeric'};
    return [
      dateTimeFormatter.format(labelStartDate, opts),
      '-',
      dateTimeFormatter.format(labelEndDate, opts)
    ];
  }

  static cleanAriaLabelsForByWeekDates(labels) {
    const ariaLabels = [];
    for (const label of labels) {
      ariaLabels.push(label.join(' '));
    }

    return ariaLabels;
  }

  /**
   * Get the start and end timestamps for the selected time period
   *
   * @param date - actual Date
   * @param timePeriod - weeks or months
   * @param increment - this can be positive or negative
   * @param product - product id
   */
  static getUTCTimestampsByIncrement(date, timePeriod, increment, product = '') {
    let startDate = null;
    let endDate = null;
    const weekStartsOn = (landingPageProducts.includes(product)) ? 1 : 0;
    if (timePeriod === timeAggregate.DAY) {
      startDate = startOfWeek(date, {weekStartsOn});
      endDate = endOfWeek(date, {weekStartsOn});
    } else if (timePeriod === timeAggregate.WEEK) {
      startDate = addWeeks(startOfWeek(date, {weekStartsOn}), increment);
      endDate = endOfWeek(date, {weekStartsOn});
    } else if (timePeriod === timeAggregate.MONTH) {
      startDate = addMonths(date, increment);
      startDate = startOfMonth(startDate);
      endDate = endOfMonth(date);
    } else if (timePeriod === timeAggregate.ALLTIME) {
      const now = new Date();
      startDate = subYears(now, 3);
      endDate = endOfDay(now);
    }

    return {
      startTimestamp: getUnixTime(this.convertDateToUTC(startDate)),
      endTimestamp: getUnixTime(this.convertDateToUTC(endDate))
    };
  }

  /**
   * Get the start and end timestamps for the selected time period
   *
   * @param date - actual Date
   * @param timePeriod - weeks or months
   * @param increment - this can be positive or negative
   */
  static getFormattedDateByIncrement(date, timePeriod, increment) {
    const {startDate, endDate} = this.getDateByIncrement(date, timePeriod, increment);
    return {
      start: format(startDate, 'yyyyMMdd'),
      end: format(endDate, 'yyyyMMdd'),
    };
  }

  /**
   *
   * @param date
   * @param timePeriod
   * @param increment
   * @returns {{endDate: null, startDate: null}}
   */
  static getDateByIncrement(date, timePeriod, increment) {
    let startDate = null;
    let endDate = null;
    if (timePeriod === timeAggregate.WEEK) {
      startDate = addWeeks(startOfWeek(date), increment);
      endDate = endOfWeek(date);
    } else if (timePeriod === timeAggregate.MONTH) {
      startDate = addMonths(date, increment);
      startDate = startOfMonth(startDate);
      endDate = endOfMonth(date);
    } else if (timePeriod === timeAggregate.ALLTIME) {
      startDate = new Date();
      endDate = addDays(startDate, 1);
    }

    return {startDate, endDate};
  }

  static getTargetLegend(args) {
    const {targetUsage, filter, average, label, legendLabels, language, footerNotices} = args;
    let data = {
      low: 0,
      med: 0,
      high: 0,
      percentage_low: 0,
      percentage_med: 0,
      percentage_high: 0,
    };
    if (targetUsage) {
      data = targetUsage;
    }
    numberAbbr.lang = language;
    const abbreviatedAverage = numberAbbr.abbr(average || 0);
    if (footerNotices?.activity?.has('*')) {
      abbreviatedAverage.postfix += '*';
    } else if (footerNotices?.activity?.has('**')) {
      abbreviatedAverage.postfix += '**';
    }
    return {
      average: abbreviatedAverage,
      label,
      keys: [
        {
          value: data.high,
          label: (data.percentage_high > 0 && data.percentage_high < 1) ? '<1' : data.percentage_high || 0,
          description: legendLabels.high,
          filterVal: targetBlocks.HIGH,
          active: (filter === targetBlocks.HIGH),
          ariaLabel: dashboardAriaLabels.high((data.percentage_high > 0 && data.percentage_high < 1) ? '<1' : data.percentage_high || 0),
        },
        {
          value: data.med,
          label: (data.percentage_med > 0 && data.percentage_med < 1) ? '<1' : data.percentage_med || 0,
          description: legendLabels.med,
          filterVal: targetBlocks.MED,
          active: (filter === targetBlocks.MED),
          ariaLabel: dashboardAriaLabels.med((data.percentage_med > 0 && data.percentage_med < 1) ? '<1' : data.percentage_med || 0),
        },
        {
          value: data.low,
          label: (data.percentage_low > 0 && data.percentage_low < 1) ? '<1' : data.percentage_low || 0,
          description: legendLabels.low,
          filterVal: targetBlocks.LOW,
          active: (filter === targetBlocks.LOW),
          ariaLabel: dashboardAriaLabels.inactive((data.percentage_low > 0 && data.percentage_low < 1) ? '<1' : data.percentage_low || 0),
        },
      ],
    };
  }

  static getHasUnlicensedOrRemovedStudents($filteredStudents) {
    let $hasUnlicensedOrRemovedStudents = false;
    for (let i = 0; i < $filteredStudents.length; i++) {
      const student = $filteredStudents[i];
      const invalidProductStatuses = [productStatuses.EXPIRED, productStatuses.UNASSIGNED];
      if (
        student.link_status !== statusConst.ACTIVE ||
        invalidProductStatuses.includes(student.product_status)
      ) {
        $hasUnlicensedOrRemovedStudents = true;
        break;
      }
    }
    return $hasUnlicensedOrRemovedStudents;
  }

  static getFormattedUsage(usage, keys = ['percentage45', 'percentage31', 'percentage16', 'percentage1']) {
    const $ret = [];
    if (!usage || Object.keys(usage).length === 0) {
      return $ret;
    }

    usage.map((item) => {
      $ret.push({
        stats: [
          {
            label: this.generatePercentageLabel(item[keys[0]] || 0),
            styles: {
              backgroundColor: colorMap.minutes45,
            },
          },
          {
            label: this.generatePercentageLabel(item[keys[1]] || 0),
            styles: {
              backgroundColor: colorMap.minutes31,
            },
          },
          {
            label: this.generatePercentageLabel(item[keys[2]] || 0),
            styles: {
              backgroundColor: colorMap.minutes16,
            },
          },
          {
            label: this.generatePercentageLabel(item[keys[3]] || 0),
            styles: {
              backgroundColor: colorMap.minutes1,
            },
          }
        ],
        students: item.total_active_students,
        hasUnlicensedOrRemovedStudents: item.has_removed_students || item.has_unlicensed_students,
        groupId: item.group_id,
        groupName: item.group_name,
        groupPath: item.group_path,
        removedInWeek: item.removed_in_week,
      });
    });
    return $ret;
  }

  static getFormattedActivityStatus(obj, addStruggling = true) {
    const {usage, colors} = obj;
    const $ret = [];
    if (!usage) {
      return $ret;
    }

    usage.map((item) => {
      const stats = [
        {
          label: this.generatePercentageLabel(item.supplemental_stats?.percentage_in_progress),
          styles: {
            backgroundColor: colors[0],
          },
        },
        {
          label: this.generatePercentageLabel(item.supplemental_stats?.percentage_struggling),
          styles: {
            backgroundColor: colors[1],
          },
        },
        {
          label: this.generatePercentageLabel(item.supplemental_stats?.percentage_inactive),
          styles: {
            backgroundColor: colors[2],
          },
        }
      ];
      $ret.push({
        stats,
        students: item.total_students,
        hasUnlicensedOrRemovedStudents: item.has_removed_students || item.has_unlicensed_students,
        groupId: item.group_id,
        groupName: item.group_name,
        groupPath: item.group_path,
        removedInWeek: item.removed_in_week,
      });
    });
    return $ret;
  }
  static getPercentages(_percentages, _values) {
    const percentages = {};

    for (const [key, percentage] of Object.entries(_percentages)) {
      const value = _values[key] || 0;
      let label = (value > 0 && percentage === 0) ? '<1' : percentage;
      // This case handles the legacy mma/mra products
      if (percentage > 0 && percentage < 1) {
        label = '<1';
      }

      percentages[key] = {
        percentage,
        label,
      };
    }

    return percentages;
  }

  static getOnTrackPercentage(_percentages, total) {
    const percentages = {};
    for (const [key, number] of Object.entries(_percentages)) {
      percentages[key] = this.getRoundedPercentage(number, total);
    }
    return percentages;
  }


  static getFormattedActivity(obj) {
    const {usage, colors} = obj;
    const $ret = [];
    if (!usage || Object.keys(usage).length === 0) {
      return $ret;
    }

    usage.map((item) => {
      $ret.push({
        stats: [
          {
            label: this.generatePercentageLabel(item.supplemental_stats?.percentage_high || 0),
            styles: {
              backgroundColor: colors[0],
            },
          },
          {
            label: this.generatePercentageLabel(item.supplemental_stats?.percentage_med || 0),
            styles: {
              backgroundColor: colors[1],
            },
          },
          {
            label: this.generatePercentageLabel(item.supplemental_stats?.percentage_low || 0),
            styles: {
              backgroundColor: colors[2],
            },
          }
        ],
        students: item.total_students,
        hasUnlicensedOrRemovedStudents: item.has_removed_students || item.has_unlicensed_students,
        groupId: item.group_id,
        groupName: item.group_name,
        groupPath: item.group_path,
        removedInWeek: item.removed_in_week
      });
    });
    return $ret;
  }

  static getFooterNoticesForStats(stats) {
    const footerNotices = new Set();
    for (const stat of stats) {
      if (stat.removed_in_week) {
        footerNotices.add('**');
      } else if (stat.has_removed_students || stat.has_unlicensed_students) {
        footerNotices.add('*');
      }

      // don't have to check rest if all notices are true already
      if (footerNotices.size === 2) {
        break;
      }
    }
    return footerNotices;
  }

  static getStudentActionItems(
    totalTimeThisWeek,
    weeklyTimeTargetSeconds,
    studentName,
    recommendedSkills
  ) {
    let $actionItems = [];
    let hasStruggling = false;
    if (recommendedSkills.length > 0) {
      hasStruggling = recommendedSkills.some(skill => skill.status === nodeStatuses.INTERVENTION);
    }

    if (hasStruggling) {
      $actionItems.push({
        icon: `<img src="${require('@assets/images/warning-sign.webp')}" alt="">`,
        count: '',
        message: dashboardLabels.getActionItemWeeklyStruggling(studentName),
        clickable: false
      });
    }
    const $timeSpentActionItems = DashboardUtils.timeSpentActionItem(totalTimeThisWeek, weeklyTimeTargetSeconds, studentName);
    $actionItems = $actionItems.concat($timeSpentActionItems);

    return {
      $actionItems
    };
  }

  static getParentStudentActionItems(
    totalTimeThisWeek,
    weeklyTimeTargetSeconds,
    studentName,
    hasStruggling
  ) {
    let $actionItems = [];
    if (hasStruggling) {
      $actionItems.push({
        icon: `<img src="${require('@assets/images/warning-sign.webp')}" alt="">`,
        count: '',
        message: dashboardLabels.getActionItemWeeklyStruggling(studentName),
        clickable: false
      });
    }

    const $timeSpentActionItems = DashboardUtils.timeSpentActionItem(totalTimeThisWeek, weeklyTimeTargetSeconds, studentName);
    $actionItems = $actionItems.concat($timeSpentActionItems);

    return {$actionItems};
  }

  static timeSpentActionItem(
    totalTimeThisWeek,
    weeklyTimeTargetSeconds,
    studentName
  ) {
    const actionItems = [];
    if (totalTimeThisWeek === 0) {
      actionItems.push({
        icon: '<span class="ai ai-inactive text-muted"></span>',
        count: '',
        message: dashboardLabels.getActionItemWeeklyTargetNotStarted(studentName),
        clickable: false
      });
    } else if (totalTimeThisWeek > weeklyTimeTargetSeconds) {
      actionItems.push({
        icon: '<span class="ai ai-trophy text-success"></span>',
        title: '',
        message: dashboardLabels.getActionItemWeeklyTargetMet(studentName, weeklyTimeTargetSeconds / 60),
        clickable: false
      });
    } else {
      actionItems.push({
        icon: '<span class="ai ai-target text-blue"></span>',
        title: '',
        message: dashboardLabels.getActionItemWeeklyTargetProgressing(studentName, weeklyTimeTargetSeconds / 60),
        clickable: false
      });
    }

    return actionItems;
  }

  static getRowsForFilters(
    progressFiltersState,
    nodes,
    conceptsMastered,
    studentsCount
  ) {
    const rowInfo = {};
    if (nodes.length === 0) {
      return rowInfo;
    }

    nodes.map((nodeInfo) => {
      const statusCounts = conceptsMastered?.[nodeInfo.id] ||
        {
          mastery_achieved: 0,
          prior_knowledge: 0,
          in_progress: 0,
          intervention: 0
        };

      const addNodeToList = DashboardUtils.shouldAddNodeToList(
        nodeInfo,
        progressFiltersState,
        statusCounts,
      );
      if (addNodeToList) {
        const total = Object.values(statusCounts).reduce((a, b) => a + b, 0);
        let formattedTotal = 0;
        if (studentsCount > 0) {
          formattedTotal = Math.floor(total / studentsCount * 100);
        }
        rowInfo[nodeInfo.id] = {
          nodeId: nodeInfo.id,
          objective: nodeInfo?.game_description || nodeInfo?.name || '',
          total,
          ...statusCounts,
          total_formatted: unsafeHTML(total + ' <span class="pl-2 text-muted"> (' + formattedTotal + '%) </span>')
        };
      }
    });
    return rowInfo;
  }

  static shouldAddNodeToList(
    skill,
    progressFilterState,
    statusCounts
  ) {
    if (!skill || Object.keys(skill).length === 0) {
      return false;
    }

    let addToList = false;
    const statusFilterMatches = (!progressFilterState.filters.statusFilter) ? true : statusCounts?.[progressFilterState.filters.statusFilter] > 0;

    if (statusFilterMatches) {
      addToList = true;
    }
    return addToList;
  }

  static formatRecommendedActivityCounts(recommendedActivity, studentInfo) {
    const reformattedLesson = {
      total: 0,
      mastery_achieved: [],
      prior_knowledge: [],
      in_progress: [],
      struggling: [],
      inactive: [],
    };
    if (Object.keys(studentInfo).length === 0) {
      return reformattedLesson;
    }
    recommendedActivity.mastery_achieved?.map((studentId) => {
      reformattedLesson.mastery_achieved.push(studentInfo[studentId]);
      reformattedLesson.total += 1;
    });
    recommendedActivity.prior_knowledge?.map((studentId) => {
      reformattedLesson.prior_knowledge.push(studentInfo[studentId]);
      reformattedLesson.total += 1;
    });
    recommendedActivity.in_progress.map((studentId) => {
      reformattedLesson.in_progress.push(studentInfo[studentId]);
      reformattedLesson.total += 1;
    });
    recommendedActivity.struggling.map((studentId) => {
      reformattedLesson.struggling.push(studentInfo[studentId]);
      reformattedLesson.total += 1;
    });
    recommendedActivity.inactive.map((studentId) => {
      reformattedLesson.inactive.push(studentInfo[studentId]);
      reformattedLesson.total += 1;
    });
    return reformattedLesson;
  }

  static getLabelsStandardsCategories(nodes) {
    const standardLabels = new Set();
    const standardCategories = {};

    for (const node in nodes) {
      for (const standard in nodes[node].external_standards) {
        const standardInfo = nodes[node].external_standards[standard];
        standardLabels.add(standardInfo.standard_set_label);

        if (typeof standardCategories[standardInfo.standard_set_label] === 'undefined') {
          standardCategories[standardInfo.standard_set_label] = [];
        }

        if (typeof standardCategories[standardInfo.standard_set_label][standardInfo.parent_taxonomy.type] === 'undefined') {
          standardCategories[standardInfo.standard_set_label][standardInfo.parent_taxonomy.type] = new Set();
        }
        standardInfo.parent_taxonomy.names.map((name) => {
          standardCategories[standardInfo.standard_set_label][standardInfo.parent_taxonomy.type].add(name);
        });
      }
    }

    return {
      standardLabels: [...standardLabels],
      standardCategories
    };
  }

  static getSortedStudents(students, order) {
    if (!students || !students.length) {
      return [];
    }
    return (order === 'asc') ?
      students.sort((a, b) => a.count - b.count) :
      students.sort((a, b) => b.count - a.count);
  }

  static roundTimeSpent(timeSpent) {
    return (timeSpent < 60) ? Math.ceil(timeSpent / 60) : Math.floor(timeSpent / 60);
  }

  static getStudentForUsage(key, student) {
    const count = this.roundTimeSpent(student.time);
    return {
      ...student,
      count,
      label: dashboardLabels.MINUTES,
      status: key,
    };
  }

  static getStudentForActivityStatus(status, student, studentActivity) {
    let count = 0;
    if (status === masteryActivityBlocks.STRUGGLING) {
      count = studentActivity[nodeStatuses.INTERVENTION];
    } else if (status === masteryActivityBlocks.IN_PROGRESS) {
      count = studentActivity[nodeStatuses.IN_PROGRESS] +
        studentActivity[nodeStatuses.PRIOR_KNOWLEDGE] +
        studentActivity[nodeStatuses.INTERVENTION] +
        studentActivity[nodeStatuses.MASTERY_ACHIEVED];
    }
    return {
      ...student,
      count,
      status,
      label: dashboardLabels.MINUTES,
    };
  }

  static getMasteryActivityChartData(args) {
    const {activityStatuses, filter} = args;
    const data = (typeof activityStatuses === 'undefined' || Object.keys(activityStatuses).length === 0) ?
      [0, 0, 0] :
      [
        activityStatuses.in_progress,
        activityStatuses.struggling,
        activityStatuses.inactive,
      ];
    const doughnutValues = [
      masteryActivityBlocks.IN_PROGRESS,
      masteryActivityBlocks.STRUGGLING,
      masteryActivityBlocks.INACTIVE
    ];
    const colors = DashboardUtils.mmaActivityMapArray;
    const ariaLabels = [
      dashboardAriaLabels.PROGRESSING,
      dashboardAriaLabels.STRUGGLING,
      dashboardAriaLabels.INACTIVE_TARGET,
    ];

    return Object.assign({}, DashboardUtils.USAGE_CHART_DATA, {
      datasets: [
        {
          data,
          backgroundColor: colors,
          hoverBackgroundColor: colors,
          borderColor: colors,
          borderWidth: DashboardUtils.getBorderWidths(filter, doughnutValues),
          borderAlign: DashboardUtils.getBorderAligns(filter, doughnutValues),
          ariaLabel: dashboardAriaLabels.STUDENTS,
          squareAriaLabels: args.squareAriaLabels,
        }
      ],
      ariaLabels,
    });
  }

  static getWeeklyTargetGoal = (product) => {
    switch (product) {
      case products.MATH_ACADEMY:
      case products.MY_MATH_ACADEMY:
        return weeklyMinuteTargetGoals.MATH_ACADEMY;
      case products.READING_MASTERY:
      case products.MY_READING_ACADEMY:
        return weeklyMinuteTargetGoals.READING_MASTERY;
      case products.ABCMOUSE:
        return weeklyMinuteTargetGoals.ABCMOUSE;
      case products.ABCMOUSE_ENGLISH:
        return weeklyMinuteTargetGoals.ABCMOUSE_ENGLISH;
    }
  };

  static skillsSearchCall(product, fields = ['id', 'name'])
  {
    const state = storeInstance.getState();
    const externalStandards = state[sdoNs.SESSION].settings?.external_standards || [];
    let aggs = ['grades'];
    if (externalStandards.length) {
      aggs = [
        'external_standards.set_id',
        'external_standards.grade',
        'external_standards.lo_parent_type',
        'external_standards.lo_parent_name'
      ];
    }
    return ApiService.skillsSearch(
      this.buildSearchSkillsFilters(product),
      this.buildSkillsSearch(),
      {
        fields,
        aggs
      },
      { limit: 1000 }
    );
  }

  static buildSkillsSearch()
  {
    const progressFiltersState = storeInstance.getState()[sdoNs.PROGRESS_FILTERS_VIEW];
    let searchFilter = null;
    if (progressFiltersState.$hasFilter) {
      if (progressFiltersState.filters.nameFilter !== '') {
        searchFilter = {
          'field': '*',
          'query': progressFiltersState.filters.nameFilter
        };
      }
    }
    return searchFilter;
  }

  static buildSearchSkillsFilters(product) {
    const state = storeInstance.getState();
    const progressFiltersState = state[sdoNs.PROGRESS_FILTERS_VIEW];
    let filters = {'product': [product]};
    if (progressFiltersState.$hasFilter) {
      if (progressFiltersState.filters.standardSetFilter) {
        filters = {...filters, 'external_standards.set_id': [progressFiltersState.filters.standardSetFilter]};
      }

      if (progressFiltersState.filters.gradeFilter) {
        const externalStandards = state[sdoNs.SESSION].settings?.external_standards || [];
        if (externalStandards.length) {
          filters = {...filters, 'external_standards.grade': [progressFiltersState.filters.gradeFilter]};
        } else {
          filters = {...filters, 'grades': [progressFiltersState.filters.gradeFilter]};
        }
      }

      if (progressFiltersState.filters.standardCategoryFilter) {
        filters = {...filters, 'external_standards.lo_parent_name': [progressFiltersState.filters.standardCategoryFilter]};
      }
    }
    return filters;
  }
  static generatePercentageLabel(percentage, val = 0) {
    if ((percentage > 0 && percentage < 1) || (percentage === 0 && val > 0)) {
      return '<1%';
    }
    return percentage + '%';
  }
  static getPercentage(numerator, denominator, convertLessThanOne = true) {
    let percentage = 0;
    if (denominator > 0) {
      percentage = numerator * 100 / denominator;
    }

    if (convertLessThanOne === true && percentage > 0 && percentage < 1) {
      return '<1';
    }

    return percentage;
  }
  static getRoundedPercentage(numerator, denominator, convertLessThanOne = true) {
    const percentage = this.getPercentage(numerator, denominator, convertLessThanOne);
    if (typeof percentage === 'string') {
      return percentage;
    }

    return Math.round(percentage);
  }

  static getRangeLabels(ranges) {
    const labels = {};
    const rangeKeys = Object.keys(ranges);

    let i = 0;
    for (const [key, sec] of Object.entries(ranges)) {
      if (typeof rangeKeys[i+1] === 'undefined') {
        labels[key] = '≥' + Math.floor(sec / 60).toString() + ' mins';
        break;
      }
      labels[key] = Math.floor(sec / 60).toString() + '-';
      labels[key] += Math.floor(((ranges[rangeKeys[i+1]]) / 60) - 1).toString() + ' mins';
      if (labels[key].charAt(0) === '0') {
        labels[key] = ">" + labels[key];
      }
      i++;
    }
    return labels;
  }
  static getTimeSpentInMinutes(timeSpentSeconds) {
    if (timeSpentSeconds < 60) {
      return Math.ceil(timeSpentSeconds/60);
    }
    return Math.floor(timeSpentSeconds/60);
  }
  static print(context, isLandscape) {
    const printdiv = document.getElementById('printdiv');
    const printcontent = document.getElementById('printcontent');
    const cz = document.getElementById('cz_success_center_launcher_frame');
    const adaButton = document.getElementById('ada-entry');
    const zendesk = document.getElementById('launcher');
    const main = document.getElementById('main');
    cz?.classList?.add('d-print-none');
    adaButton?.classList?.add('d-print-none');
    zendesk?.classList?.add('d-print-none');
    main?.classList?.add('d-print-none');
    printdiv.classList.remove('d-none');
    if (isLandscape) {
      printdiv.classList.add('landscape');
      document.body.classList.add('page-landscape');
    } else {
      printdiv.classList.add('portrait');
      document.body.classList.add('page-portrait');
    }
    window.target = document.createElement('div');
    context.before(window.target);
    printcontent.append(context);
    window.print();
    printdiv.classList.add('d-none');
    printdiv.classList.remove('portrait', 'landscape');
    document.body.classList.remove('page-portrait', 'page-landscape');
    window.target.before(printcontent.childNodes[0]);
  }

  static printWithHeader(context, isLandscape, header) {
    const printdiv = document.getElementById('print-with-header');
    const printcontent = printdiv.shadowRoot.getElementById('print-with-header-content');
    const cz = document.getElementById('cz_success_center_launcher_frame');
    const zendesk = document.getElementById('launcher');
    const main = document.getElementById('main');
    cz?.classList?.add('d-print-none');
    zendesk?.classList?.add('d-print-none');
    main?.classList?.add('d-print-none');
    printdiv.classList.remove('d-none');
    if (isLandscape) {
      printdiv.classList.add('landscape');
      document.body.classList.add('page-landscape');
    } else {
      printdiv.classList.add('portrait');
      document.body.classList.add('page-portrait');
    }
    window.target = document.createElement('div');
    context.before(window.target);
    printcontent.append(context);
    window.print();
    printdiv.classList.add('d-none');
    printdiv.classList.remove('portrait', 'landscape');
    document.body.classList.remove('page-portrait', 'page-landscape');
    window.target.before(printcontent.childNodes[0]);
  }
}

class DateHelper {
  static getStartEndTimestampsForWeek(startDate, endDate, weeksToMove) {
    const dateDiff = differenceInWeeks(endDate, startDate);
    const newStartDate = addWeeks(startDate, weeksToMove);
    const newEndDate = addWeeks(newStartDate, dateDiff);

    return {
      start: newStartDate.getTime() / 1000,
      end: newEndDate.getTime() / 1000
    };
  }
}

class ChartJSHelpers {
  static studentActivityChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 6,
    datasets: {
      bar: {
        barPercentage: .5
      }
    },
    scales: {
      xAxis: {
        stacked: true,
        offset: true,
        ticks: {
          color: '#000',
          font: {
            family: 'aofl-sans',
            size: 14,
            weight: 600,
          },
        },
        grid: {
          display: false,
        },
      },
      yAxis: {
        position: 'left',
        min: 0,
        max: 100,
        stacked: true,
        grid: {
          color: '#ddd',
          display: true,
          drawBorder: false,
          tickLength: 20,
        },
        ticks: {
          color: '#fff',
          padding: 6,
        }
      },
    },
    animation: false,
    plugins: {
      legend: false,
      tooltip: false,
      datalabels: {
        anchor: 'center',
        align(context) {
          const index = context.dataIndex;
          const value = context.dataset.data[index];
          const hasData = context.dataset.hasData[index];
          let displayZero = (context.datasetIndex === 0);

          if (hasData) {
            displayZero = value > 0;
          }
          return displayZero ? 'top' : 'center';
        },
        color: 'black',
        font: {
          weight: 'bold'
        },
        display(context) {
          const index = context.dataIndex;
          const value = context.dataset.data[index];
          const hasData = context.dataset.hasData[index];
          let display = (context.datasetIndex === 0);

          if (hasData) {
            display = value > 0;
          }
          return display;
        },
        formatter(value, context) {
          return value + '%';
        }
      },
      aofl_accessibility: {
        formatter: (item) => {
          const label = item.key;
          return `${label}: ${item.value}`;
        }
      }
    }
  };

  static rosteredActiveChartOptions = {
    responsive: true,
    aspectRatio: 6,
    maintainAspectRatio: false,
    datasets: {
      bar: {
        barPercentage: .5
      }
    },
    animation: false,
    scales: {
      xAxis: {
        offset: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#000',
          font: {
            family: 'aofl-sans',
            size: 14,
            weight: 600,
          },
        }
      },
      yAxis: {
        position: 'left',
        min: 0,
        max: 100,
        grace: '10%',
        grid: {
          color: '#ddd',
          display: true,
          drawBorder: false,
          tickLength: 20,
        },
        ticks: {
          color: '#000',
          font: {
            family: 'aofl-sans',
            size: 14,
            weight: '700',
          },
          padding: 6,
          callback(value, index, values) {
            return value + '%';
          }
        }
      },
    },
    plugins: {
      legend: false,
      tooltip: false,
      datalabels: {
        anchor: 'end',
        align: 'end',
        color: 'black',
        font: {
          weight: 'bold'
        },
        display: true,
        formatter(value, context) {
          return value + '%';
        }
      },
      aofl_accessibility: {
        formatter: (item) => {
          const label = item.key;
          return `${label}: ${item.value}`;
        }
      }
    }
  };
}

export {
  DashboardUtils,
  DateHelper,
  ChartJSHelpers
};
